import { toast } from 'sonner'
import { hmrcAuthService } from './hmrcAuthService'
import { ApiError } from './api'

/**
 * Extract auth URL from HMRC authorization response
 */
const extractAuthUrl = (data: any): string | null => {
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      return parsed?.authUrl || parsed?.data?.authUrl || null
    } catch {
      return null
    }
  }
  if (data?.authUrl) return data.authUrl
  if (data?.data?.authUrl) return data.data.authUrl
  if (data?.response?.authUrl) return data.response.authUrl
  return null
}

/**
 * Check if a filing option requires HMRC authorization
 * Companies House Filing has its own separate OAuth flow
 * SA Online Flow uses email/password only - no HMRC auth needed
 */
export const requiresHmrcAuth = (optionId: string): boolean => {
  // Only these filing options require HMRC authorization
  // Companies House Filing uses its own OAuth flow
  // self-assessment-sa (SA Online) does NOT require HMRC auth
  return [
    'add-company',
    'self-assessment',
    'self-assessment-trad',
    'ct600-filing',
  ].includes(optionId)
}

/**
 * Get the destination route for a filing option
 */
export const getFilingRoute = (optionId: string): string | null => {
  const routeMap: Record<string, string> = {
    'add-company': '/add-company',
    'self-assessment': '/add-self-assessment',
    'self-assessment-sa': '/add-self-assessment?flow=sa',
    'self-assessment-trad': '/add-self-assessment?flow=traditional',
    'ct600-filing': '/ct600-filing',
    'companies-house-filing': '/companies-house-filing', // Adjust if needed
  }
  return routeMap[optionId] || null
}

/**
 * Initiate HMRC authorization and redirect user
 * Returns the auth URL if successful, null otherwise
 */
export const initiateHmrcAuth = async (
  companyId?: string
): Promise<string | null> => {
  try {
    const envelope = await hmrcAuthService.authorize(
      companyId ? { companyId } : undefined
    )

    // The envelope has { statusCode, response: HmrcAuthorizeResponse, message?, error? }
    const authResponse = envelope.response

    // Check if the response has authUrl directly
    if (authResponse?.authUrl) {
      return authResponse.authUrl
    }

    // Try to extract from nested structure
    const authUrl = extractAuthUrl(authResponse) ?? extractAuthUrl(envelope)

    if (!authUrl) {
      toast.error(
        'Unable to generate HMRC authorization link. Please try again later.'
      )
      return null
    }

    return authUrl
  } catch (err) {
    console.error('HMRC authorization initiation failed:', err)
    if (err instanceof ApiError) {
      toast.error(
        err.message ||
          'Unable to start HMRC authorization. Please try again later.'
      )
    } else {
      toast.error('An unexpected error occurred. Please try again later.')
    }
    return null
  }
}

/**
 * Handle filing option selection with HMRC auth check
 * Returns the auth URL and route for the modal to handle
 * @param optionId - The filing option ID
 * @param companyId - Optional company ID for company-specific auth
 * @returns Object with route and authUrl, or null if invalid
 */
export const prepareHmrcAuth = async (
  optionId: string,
  companyId?: string
): Promise<{ route: string; authUrl: string | null } | null> => {
  // Get destination route
  const route = getFilingRoute(optionId)
  if (!route) {
    toast.error('Invalid filing option selected.')
    return null
  }

  // Store destination route for after auth completion
  sessionStorage.setItem('hmrcAuthRedirect', route)

  // Initiate HMRC authorization
  const authUrl = await initiateHmrcAuth(companyId)

  return { route, authUrl }
}
